import { COLORS } from "@coeff/utils";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { Tooltip } from "../../../common";
import { Input } from "../../../common";

import { LookerConnectionInfo } from "./AuthorizeLookerContentV2";

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
`;

const StyledInput = styled.label`
  display: block;
  width: auto;
  margin: 16px;

  .coeff__input {
    height: 32px;
    margin: auto;
    border-color: ${COLORS.black15};
  }
`;

const StyledAnchor = styled.a`
  color: ${COLORS.coeblue4};
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  :hover {
    color: ${COLORS.coeblue4};
  }
`;

export const validateLookerInputs = ({ url, clientId, clientSecret }: LookerConnectionInfo) => {
  const result: Partial<Record<keyof LookerConnectionInfo, string>> = {};
  if (!url) {
    result.url = "URL is required";
  }
  if (!clientId) {
    result.clientId = "Client ID is required";
  }
  if (!clientSecret) {
    result.clientSecret = "Client Secret is required";
  }
  return Object.keys(result).length ? result : null;
};

export const FormStep: React.FC<{
  readOnly: boolean;
  error?: Partial<LookerConnectionInfo>;
  connectionInfo: LookerConnectionInfo;
  onChanges: (values: LookerConnectionInfo) => void;
  dataSourceId: string;
}> = ({ readOnly, onChanges, error, connectionInfo, dataSourceId }) => {
  const [clientUrl, setClientUrl] = useState(connectionInfo.url);

  const [clientId, setClientId] = useState(connectionInfo.clientId);

  const [clientSecret, setClientSecret] = useState(connectionInfo.clientSecret);

  useEffect(() => {
    onChanges({
      clientId: clientId,
      clientSecret: clientSecret,
      url: clientUrl,
    });
  }, [clientUrl, clientId, clientSecret, onChanges]);

  return (
    <>
      <Tooltip title={readOnly || !!dataSourceId ? "Base URL cannot be edited" : ""}>
        <StyledInput>
          <LabelContainer>Base URL</LabelContainer>
          <Input
            size="middle"
            onChange={e => setClientUrl(e.currentTarget.value)}
            placeholder="https://example.cloud.looker.com"
            error={!!error?.url}
            value={clientUrl}
            disabled={readOnly || !!dataSourceId}
            title="Base URL cannot be edited"
          />
          {error?.url && <Typography color="error">{error.url}</Typography>}
          <Typography
            fontSize={12}
            lineHeight={"14px"}
            color="textSecondary"
            style={{ marginTop: 2 }}
          >
            The URL you use to access your Looker account
          </Typography>
        </StyledInput>
      </Tooltip>

      <StyledInput>
        <LabelContainer>
          Client ID
          <Typography fontSize={12} lineHeight={"22px"}>
            <StyledAnchor
              tabIndex={-1}
              href="https://help.coefficient.io/hc/en-us/articles/17969072042907-Looker#h_01HRCDKYN8NFMZDXTJR5CRPHFV"
              target="_blank"
            >
              How to find
            </StyledAnchor>
          </Typography>
        </LabelContainer>
        <Input
          size="middle"
          onChange={e => setClientId(e.currentTarget.value)}
          error={!!error?.clientId}
          value={clientId}
          disabled={readOnly}
        />
        {error?.clientId && <Typography color="error">{error.clientId}</Typography>}
      </StyledInput>

      <StyledInput>
        <LabelContainer>
          Client Secret
          <Typography fontSize={12}>
            <StyledAnchor
              tabIndex={-1}
              href="https://help.coefficient.io/hc/en-us/articles/17969072042907-Looker#h_01HRCDKYN8NFMZDXTJR5CRPHFV"
              target="_blank"
            >
              How to find
            </StyledAnchor>
          </Typography>
        </LabelContainer>
        <Input
          type="password"
          size="middle"
          autoComplete="off"
          onChange={e => setClientSecret(e.currentTarget.value)}
          error={!!error?.clientSecret}
          value={clientSecret}
          disabled={readOnly}
        />
        {error?.clientSecret && <Typography color="error">{error.clientSecret}</Typography>}
      </StyledInput>
    </>
  );
};
